import { DestroyRef, inject, Injectable } from '@angular/core';
import { of, shareReplay } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import { isNull, isUndefined } from '../utils';
import { AuthService } from './auth.service';
import { FileCacheService } from './file-cache.service';
import { WatchdogService } from './watchdog.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WebsocketService } from './websocket.service';
import { KVRepository } from '../repositories';
import { FileCacheRepository } from '../repositories/file-cache.repository';
import { FileCacheModel } from '../models';
import { ClusterService } from './cluster.service';

@Injectable()
export class InvoiceQrcodeImageService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly auth = inject(AuthService);
  private readonly cluster = inject(ClusterService);
  private readonly watchdog = inject(WatchdogService);
  private readonly filesCacheRepository = inject(FileCacheRepository);
  private readonly kvRepository = inject(KVRepository);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly webSocketService = inject(WebsocketService);
  private readonly filesCache = inject(FileCacheService);


  private readonly storageKey = 'invoiceQrcodeImage';
  private readonly logger = this.watchdog.tag('Invoice QR-Code Image', 'green');

  public readonly url$ = this.kvRepository.one$(this.storageKey).pipe(
    map((entry) => {
      if (entry?.value) {
        return entry.value as string;
      }

      return null;
    }),
    shareReplay(1),
  );

  public readonly file$ = this.url$.pipe(
    switchMap((url) => {
      if (isNull(url)) {
        return of(null);
      }

      return this.filesCacheRepository.one$(url).pipe(
        map((entry) => {
          if (isUndefined(entry)) {
            return null;
          }

          const file = entry
            ? new FileCacheModel(entry)
            : null;

          return file
            ? this.domSanitizer.bypassSecurityTrustUrl(file.objectUrl)
            : null;
        }),
      );
    }),
    shareReplay(1),
  );

  public initialize() {
    this.logger.info('Initialize');

    // Clear invoice QR-Code on logout
    this.cluster.leader$.pipe(
      filter((leader) => !!leader),
      switchMap(() => this.auth.logouted$),
      switchMap(() => this.clear()),
      tap(() => this.logger.info('Cleared invoice QR-Code on logout')),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    this.cluster.leader$.pipe(
      filter((leader) => !!leader),
      switchMap(() => this.webSocketService.messages$),
      filter((message) => message.type === 'tableInfo'),
      switchMap((response) => {
        if (!response.data.invoiceQRCodeUrl) {
          return this.clear();
        }

        return this.update(response.data.invoiceQRCodeUrl);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  public update(url: string) {
    return this.url$.pipe(
      take(1),
      switchMap((qrUrl) => {
        if (qrUrl && qrUrl === url) {
          return this.filesCache.downloadFile(url).pipe(
            map(() => qrUrl),
          );
        }

        return this.filesCache.downloadFile(url).pipe(
          switchMap(() => this.kvRepository.update$({
            key: this.storageKey,
            value: url,
          })),
          switchMap((value) => {
            if (!qrUrl) {
              return of(value);
            }

            return this.filesCacheRepository.delete$(qrUrl).pipe(
              map(() => value),
            );
          }),
          tap((value) => this.logger.info('Updated Invoice Qrcode', value)),
        );
      }),
    );
  }

  public clear() {
    return this.kvRepository.one$(this.storageKey).pipe(
      take(1),
      switchMap((qrUrl) => {
        if (isUndefined(qrUrl)) {
          return of(qrUrl);
        }

        return this.filesCacheRepository.delete$(qrUrl.value as string);
      }),
      switchMap(() => this.kvRepository.delete$(this.storageKey)),
    );
  }

}
